import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '../config/firebase';
import { 
  ADD_FCM_TOKEN, 
  UPDATE_FCM_TOKEN, 
  DELETE_ALL_USER_TOKENS, 
  GET_USER_TOKENS 
} from '../graphql/chat';
import { playNotificationSound, playCustomSound, shouldShowNotification } from '../utils/notificationUtils';

/**
 * FCM Service for managing Firebase Cloud Messaging tokens
 */
class FCMService {
  constructor() {
    this.currentToken = null;
    this.apolloClient = null;
    this.messageListenerSetup = false;
  }

  /**
   * Set Apollo Client instance
   */
  setApolloClient(client) {
    this.apolloClient = client;
  }

  /**
   * Get device information in mobile app format
   */
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    
    // Detect browser
    let browserName = 'Unknown Browser';
    let browserVersion = '';
    
    if (userAgent.indexOf('Edg/') > -1) {
      browserName = 'Microsoft Edge';
      browserVersion = userAgent.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
    } else if (userAgent.indexOf('Chrome/') > -1) {
      browserName = 'Google Chrome';
      browserVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
    } else if (userAgent.indexOf('Safari/') > -1 && userAgent.indexOf('Chrome') === -1) {
      browserName = 'Safari';
      browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || '';
    } else if (userAgent.indexOf('Firefox/') > -1) {
      browserName = 'Firefox';
      browserVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
    }
    
    // Detect OS
    let osName = 'Unknown OS';
    let osVersion = '';
    
    if (userAgent.indexOf('Win') > -1) {
      osName = 'Windows';
      if (userAgent.indexOf('Windows NT 10.0') > -1) osVersion = '10';
      else if (userAgent.indexOf('Windows NT 11.0') > -1) osVersion = '11';
      else if (userAgent.indexOf('Windows NT 6.3') > -1) osVersion = '8.1';
      else if (userAgent.indexOf('Windows NT 6.2') > -1) osVersion = '8';
      else if (userAgent.indexOf('Windows NT 6.1') > -1) osVersion = '7';
    } else if (userAgent.indexOf('Mac') > -1) {
      osName = 'macOS';
      const macVersion = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/)?.[1];
      osVersion = macVersion ? macVersion.replace(/_/g, '.') : '';
    } else if (userAgent.indexOf('Linux') > -1) {
      osName = 'Linux';
    } else if (userAgent.indexOf('Android') > -1) {
      osName = 'Android';
      osVersion = userAgent.match(/Android (\d+\.\d+)/)?.[1] || '';
    } else if (userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) {
      osName = 'iOS';
      osVersion = userAgent.match(/OS (\d+[._]\d+[._]?\d*)/)?.[1]?.replace(/_/g, '.') || '';
    }
    
    // Get app version from package.json or use default
    const appVersion = process.env.REACT_APP_VERSION || '1.0.0';
    
    // Create device info object matching mobile app format
    const deviceInfo = {
      platform: 'web',
      appVersion: appVersion,
      osVersion: osVersion || 'Unknown',
      deviceModel: browserName,
      deviceName: `${browserName} on ${osName}`,
      userAgent: `HammerHead-Web/${appVersion} (${osName} ${osVersion})`
    };
    
    return JSON.stringify(deviceInfo);
  }

  /**
   * Request notification permission and get FCM token
   */
  async requestPermissionAndGetToken() {
    try {
      // Check if messaging is supported
      if (!messaging) {
        console.warn('Firebase Messaging is not supported in this browser');
        return null;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return null;
      }

      // Get FCM token
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      
      if (!vapidKey) {
        console.error('VAPID key is not configured. Please add REACT_APP_FIREBASE_VAPID_KEY to your .env file');
        return null;
      }

      const token = await getToken(messaging, { vapidKey });
      
      if (token) {
        console.log('FCM Token generated:', token);
        this.currentToken = token;
        return token;
      } else {
        console.log('No registration token available');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  /**
   * Save FCM token to database
   */
  async saveTokenToDatabase(userId) {
    try {
      if (!this.apolloClient) {
        console.error('Apollo Client not initialized');
        return null;
      }

      // Get existing tokens for this user
      const { data: existingData } = await this.apolloClient.query({
        query: GET_USER_TOKENS,
        variables: { userId },
        fetchPolicy: 'network-only'
      });

      const deviceInfo = this.getDeviceInfo();
      const platform = 'web';

      // Check if token already exists
      const existingTokens = existingData?.userTokensList?.items || [];
      const existingToken = existingTokens.find(
        (t) => t.deviceInfo === deviceInfo && t.platform === platform
      );

      if (existingToken) {
        // Update existing token
        const { data } = await this.apolloClient.mutate({
          mutation: UPDATE_FCM_TOKEN,
          variables: {
            tokenId: existingToken.id,
            token: this.currentToken
          }
        });
        console.log('FCM token updated in database:', data);
        return data.userTokenUpdate;
      } else {
        // Create new token
        const { data } = await this.apolloClient.mutate({
          mutation: ADD_FCM_TOKEN,
          variables: {
            userId,
            token: this.currentToken,
            deviceInfo,
            platform
          }
        });
        console.log('FCM token saved to database:', data);
        return data.userTokenCreate;
      }
    } catch (error) {
      console.error('Error saving FCM token to database:', error);
      return null;
    }
  }

  /**
   * Initialize FCM for a user (request permission, get token, save to DB)
   * Note: Message listener should be set up separately via setupMessageListener()
   * to ensure navigate and enqueueSnackbar are available
   */
  async initializeForUser(userId, apolloClient) {
    try {
      this.setApolloClient(apolloClient);

      const token = await this.requestPermissionAndGetToken();
      
      if (token && userId) {
        await this.saveTokenToDatabase(userId);
      }

      return token;
    } catch (error) {
      console.error('Error initializing FCM for user:', error);
      return null;
    }
  }

  /**
   * Delete all FCM tokens for a user
   */
  async deleteUserTokens(userId, apolloClient) {
    try {
      if (!apolloClient) {
        apolloClient = this.apolloClient;
      }

      if (!apolloClient) {
        console.error('Apollo Client not initialized');
        return null;
      }

      const { data } = await apolloClient.mutate({
        mutation: DELETE_ALL_USER_TOKENS,
        variables: { userId }
      });

      console.log('FCM tokens deleted from database:', data);
      this.currentToken = null;
      return data.userTokenDeleteMany;
    } catch (error) {
      console.error('Error deleting FCM tokens:', error);
      return null;
    }
  }

  /**
   * Set up foreground message listener
   * Can be called multiple times, but will only set up once
   */
  setupMessageListener(navigate, enqueueSnackbar) {
    if (!messaging) {
      console.warn('FCM: Messaging not available');
      return;
    }

    // Prevent setting up multiple listeners
    if (this.messageListenerSetup) {
      console.log('FCM: Message listener already set up, skipping');
      return;
    }

    console.log('FCM: Setting up message listener', {
      hasNavigate: !!navigate,
      hasSnackbar: !!enqueueSnackbar
    });

    this.messageListenerSetup = true;

    onMessage(messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      
      // Display notification
      if (payload.notification) {
        const { title, body, icon } = payload.notification;
        const data = payload.data || {};
        
        // Play sound for chat notifications (respects DND and sound preferences)
        if (data.type === 'chat_message' && shouldShowNotification()) {
          // Play custom sound file for chat notifications
          // Default path: /sounds/chat-notification.mp3
          // You can change this path to your custom sound file
          const chatSoundPath = '/sounds/chat-notification.mp3';
          playCustomSound(chatSoundPath, 0.6);
        }
        
        // Dispatch custom event for chat messages so ChatPage can refetch messages
        if (data.type === 'chat_message' && data.chatId) {
          window.dispatchEvent(new CustomEvent('fcmChatMessage', {
            detail: {
              chatId: data.chatId,
              chatMessageId: data.chatMessageId,
              message: body
            }
          }));
        }
        
        // Show in-app snackbar notification
        if (enqueueSnackbar) {
          const handleClick = () => {
            console.log('Snackbar notification clicked:', data);
            
            // Handle chat messages
            if (data.type === 'chat_message' && data.chatId) {
              console.log('Navigating to chat:', data.chatId);
              
              // Navigate to chat page with the specific chat
              sessionStorage.setItem('openChatId', data.chatId);
              
              // Navigate to chat page
              if (navigate) {
                navigate('/chat');
              } else {
                window.location.href = '/chat';
              }
            }
          };

          // Create rich notification content
          const messageContent = (
            <div 
              onClick={handleClick}
              style={{ 
                cursor: data.type === 'chat_message' ? 'pointer' : 'default',
                maxWidth: '300px'
              }}
            >
              <div style={{ 
                fontWeight: 'bold', 
                fontSize: '14px',
                marginBottom: body ? '4px' : '0'
              }}>
                {title || 'New Message'}
              </div>
              {body && (
                <div style={{ 
                  fontSize: '13px', 
                  opacity: 0.9,
                  lineHeight: '1.4',
                  wordWrap: 'break-word'
                }}>
                  {body}
                </div>
              )}
              {data.type === 'chat_message' && (
                <div style={{ 
                  fontSize: '11px', 
                  marginTop: '4px',
                  opacity: 0.7,
                  fontStyle: 'italic'
                }}>
                  Click to view conversation
                </div>
              )}
            </div>
          );

          enqueueSnackbar(messageContent, {
            variant: 'info',
            autoHideDuration: 6000,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          });
        }
        
        // Also show browser notification if permission granted (as backup)
        if (Notification.permission === 'granted' && !enqueueSnackbar) {
          // Create browser notification
          const notification = new Notification(title, {
            body,
            icon: icon || '/logo/logo_single.svg',
            badge: '/logo/logo_single.svg',
            tag: payload.messageId || 'default',
            requireInteraction: false,
            data: data
          });

          // Handle notification click
          notification.onclick = (event) => {
            event.preventDefault();
            
            console.log('Browser notification clicked:', data);
            
            // Handle chat messages
            if (data.type === 'chat_message' && data.chatId) {
              window.focus();
              sessionStorage.setItem('openChatId', data.chatId);
              
              if (navigate) {
                navigate('/chat');
              } else {
                window.location.href = '/chat';
              }
            } else {
              window.focus();
            }
            
            notification.close();
          };
        }
      }
    });
  }

  /**
   * Get current token
   */
  getCurrentToken() {
    return this.currentToken;
  }
}

// Export singleton instance
export default new FCMService();

